import type { WizardField, WizardFlow } from './vysorFlows';
import { normalizeApplicationSlug, suggestApplicationSlug, validateApplicationSlug } from './applicationSlug';
import {
  ADD_INSTANCE_SUBFLOW_FIELDS,
  buildSsoPrefillFromWizard,
  buildSsoWizardChatMessage,
  buildWizardStoragePayload,
} from './vysorFlows';
import type { VysorAppCatalogItem, VysorIdpOption } from './vysorCatalog';
import { findApp, instancesForField } from './vysorCatalog';
import { isMetadataXmlContent, parseSamlMetadataContent } from './vysorMetadata';
import { idpPlatformLabel, pickDefaultIdpPlatform, realConfiguredIdps } from './vysorIdpPlatformStep';
import { inferBusinessFunctionFromName } from './vysorSetupAppAssistStep';

export interface WizardQuickOption {
  id: string;
  label: string;
  description?: string;
  value: string;
}

export interface WizardState {
  flow: WizardFlow;
  stepIndex: number;
  collected: Record<string, string>;
  /** Inline sub-wizard (e.g. create environment without leaving VYSOR). */
  subFlow?: 'add-instance';
  subFlowStepIndex?: number;
  /** User chose "Not sure" — accept references / agent help for SAML vs OIDC. */
  ssoProtocolAssistActive?: boolean;
  /** Active assist step field key (sso_type, platform, metadata_or_notes, business_function, risk_level). */
  assistFieldKey?: string;
  /** Chip-list page (1-based) for apps / environments / IdPs / long choices. */
  listPage?: number;
  /** Optional text filter for the active chip list. */
  listFilter?: string;
  /** When true, next free-text answer is treated as a list filter. */
  awaitingListFilter?: boolean;
  /** @deprecated use listPage */
  catalogPage?: number;
  /** @deprecated use listFilter */
  catalogFilter?: string;
  /** @deprecated use awaitingListFilter */
  awaitingCatalogFilter?: boolean;
}

export interface VysorWizardContext {
  configuredIdps?: VysorIdpOption[];
  canSetupApp?: boolean;
  /** Current chip-list page when building options. */
  listPage?: number;
  listFilter?: string;
  /** @deprecated use listPage */
  catalogPage?: number;
  /** @deprecated use listFilter */
  catalogFilter?: string;
}

export type VysorWizardAction =
  | 'setup_app'
  | 'browse_apps'
  | 'add_instance'
  | 'change_application'
  | 'refresh_catalog';

export interface WizardAdvanceResult {
  kind:
    | 'cancelled'
    | 'invalid'
    | 'next'
    | 'complete-navigate'
    | 'complete-sso-chat'
    | 'guided-action'
    | 'repaint-options';
  collected?: Record<string, string>;
  nextField?: WizardField;
  nextStepIndex?: number;
  userFacingAnswer?: string;
  ssoChatMessage?: string;
  ssoPrefill?: ReturnType<typeof buildSsoPrefillFromWizard>;
  storagePayload?: Record<string, string>;
  action?: VysorWizardAction;
  invalidHint?: string;
  /** Updated wizard state for list paging / filter (repaint-options). */
  wizardPatch?: Partial<WizardState>;
  pageMessage?: string;
}

/** Match pending SSO list density — short pages + next/prev chips. */
export const WIZARD_LIST_PAGE_SIZE = 12;
/** @deprecated use WIZARD_LIST_PAGE_SIZE */
export const WIZARD_APP_PAGE_SIZE = WIZARD_LIST_PAGE_SIZE;

const WIZARD_ACTION_VALUES: Record<string, VysorWizardAction> = {
  __action_setup_app__: 'setup_app',
  __action_browse_apps__: 'browse_apps',
  __action_add_instance__: 'add_instance',
  __action_change_application__: 'change_application',
  __action_refresh_catalog__: 'refresh_catalog',
};

type WizardListItem = {
  id: string;
  label: string;
  description?: string;
  value: string;
  searchText: string;
};

function wizardListPage(state: { listPage?: number; catalogPage?: number } | undefined): number {
  return state?.listPage || state?.catalogPage || 1;
}

function wizardListFilter(state: { listFilter?: string; catalogFilter?: string } | undefined): string | undefined {
  return state?.listFilter ?? state?.catalogFilter;
}

function wizardAwaitingListFilter(state: {
  awaitingListFilter?: boolean;
  awaitingCatalogFilter?: boolean;
} | undefined): boolean {
  return !!(state?.awaitingListFilter || state?.awaitingCatalogFilter);
}

export function isWizardChipListField(field: WizardField): boolean {
  return (
    field.type === 'application'
    || field.type === 'instance'
    || field.type === 'idp-platform'
    || field.type === 'choice'
    || !!field.options?.length
  );
}

function listNoun(field: WizardField): { singular: string; plural: string } {
  if (field.type === 'application') return { singular: 'application', plural: 'applications' };
  if (field.type === 'instance') return { singular: 'environment', plural: 'environments' };
  if (field.type === 'idp-platform') return { singular: 'identity provider', plural: 'identity providers' };
  return { singular: 'option', plural: 'options' };
}

function filterListItems(items: WizardListItem[], filter?: string): WizardListItem[] {
  const q = (filter || '').trim().toLowerCase();
  if (!q) return items;
  return items.filter(item => item.searchText.toLowerCase().includes(q));
}

function collectWizardListItems(
  field: WizardField,
  catalog: VysorAppCatalogItem[],
  collected: Record<string, string>,
  context: VysorWizardContext,
): WizardListItem[] {
  if (field.type === 'application') {
    return catalog.map(app => {
      const meta = [app.business_function, app.risk_level].filter(Boolean).join(' · ');
      return {
        id: `app:${app.id}`,
        label: app.name,
        description: meta ? `${meta} · ${app.instances.length} env(s)` : `${app.instances.length} env(s)`,
        value: app.id,
        searchText: [app.name, app.application_slug, app.business_function].filter(Boolean).join(' '),
      };
    });
  }
  if (field.type === 'instance') {
    return instancesForField(catalog, collected, field.instanceFilter || 'all').map(inst => ({
      id: `inst:${inst.id}`,
      label: `${inst.name} (${inst.environment})`,
      description: inst.sso_status ? `SSO: ${inst.sso_status}` : 'SSO: Not started',
      value: inst.id,
      searchText: `${inst.name} ${inst.environment} ${inst.sso_status || ''}`,
    }));
  }
  if (field.type === 'idp-platform') {
    const idps = context.configuredIdps?.length
      ? context.configuredIdps
      : [
          { id: 'generic_saml', label: 'Generic SAML' },
          { id: 'generic_oidc', label: 'Generic OIDC' },
        ];
    return idps.map(idp => ({
      id: `idp:${idp.id}`,
      label: idp.label,
      value: idp.id,
      searchText: `${idp.label} ${idp.id}`,
    }));
  }
  if (field.options?.length) {
    return field.options.map((o, i) => ({
      id: `wz_opt_${i}`,
      label: o,
      value: o,
      searchText: o,
    }));
  }
  return [];
}

function appendListNavOptions(
  opts: WizardQuickOption[],
  page: number,
  pageCount: number,
  filter: string | undefined,
  total: number,
): void {
  if (page < pageCount) {
    opts.push({
      id: 'wz_list_next',
      label: `Show next (${page + 1}/${pageCount})`,
      description: 'More items',
      value: '__list_next__',
    });
  }
  if (page > 1) {
    opts.push({
      id: 'wz_list_prev',
      label: `Show previous (${page - 1}/${pageCount})`,
      description: 'Earlier items',
      value: '__list_prev__',
    });
  }
  if (total > WIZARD_LIST_PAGE_SIZE || filter) {
    opts.push({
      id: 'wz_list_filter',
      label: 'Filter by name',
      description: 'Narrow this list',
      value: '__list_filter__',
    });
  }
  if (filter) {
    opts.push({
      id: 'wz_list_clear_filter',
      label: 'Clear filter',
      description: 'Show the full list again (paged)',
      value: '__list_clear_filter__',
    });
  }
}

export function buildWizardListPickerMessage(
  field: WizardField,
  catalog: VysorAppCatalogItem[],
  collected: Record<string, string> = {},
  context: VysorWizardContext = {},
  page = 1,
  filter?: string,
): string {
  const { singular, plural } = listNoun(field);
  const items = filterListItems(collectWizardListItems(field, catalog, collected, context), filter);
  const total = items.length;
  if (!total) {
    return filter
      ? `No ${plural} match **${filter}**. Tap **Clear filter** or try another name.`
      : field.question;
  }
  const pageCount = Math.max(1, Math.ceil(total / WIZARD_LIST_PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), pageCount);
  if (pageCount <= 1 && !filter) {
    return field.hint ? `${field.question}\n\n_${field.hint}_` : field.question;
  }
  const start = (safePage - 1) * WIZARD_LIST_PAGE_SIZE;
  const end = Math.min(start + WIZARD_LIST_PAGE_SIZE, total);
  const filterNote = filter ? ` matching **${filter}**` : '';
  return (
    `${field.question}\n\n` +
    `_**${total}** ${total === 1 ? singular : plural}${filterNote} — page **${safePage}/${pageCount}** (showing ${start + 1}–${end}). ` +
    `Tap a chip, type an exact name, or use **Filter by name** / **Show next**._`
  );
}

/** @deprecated use buildWizardListPickerMessage */
export function buildApplicationPickerMessage(
  catalog: VysorAppCatalogItem[],
  page = 1,
  filter?: string,
): string {
  return buildWizardListPickerMessage(
    {
      key: 'application_id',
      required: true,
      type: 'application',
      question: 'Which application should we use?',
    },
    catalog,
    {},
    {},
    page,
    filter,
  );
}

function clearListPagingState(): Pick<WizardState, 'listPage' | 'listFilter' | 'awaitingListFilter' | 'catalogPage' | 'catalogFilter' | 'awaitingCatalogFilter'> {
  return {
    listPage: 1,
    listFilter: undefined,
    awaitingListFilter: false,
    catalogPage: undefined,
    catalogFilter: undefined,
    awaitingCatalogFilter: false,
  };
}

export function buildWizardOptions(
  field: WizardField,
  catalog: VysorAppCatalogItem[],
  collected: Record<string, string>,
  context: VysorWizardContext = {},
): WizardQuickOption[] {
  const opts: WizardQuickOption[] = [];
  const listFilter = wizardListFilter(context);
  const requestedPage = wizardListPage(context);

  if (field.type === 'application' && !catalog.length) {
    if (context.canSetupApp) {
      opts.push({
        id: 'action_setup_app',
        label: 'Register new application',
        description: 'Run the setup wizard to add your first app',
        value: '__action_setup_app__',
      });
    }
    opts.push({
      id: 'action_browse_apps',
      label: 'Refresh catalog',
      description: 'Reload applications after someone adds one',
      value: '__action_refresh_catalog__',
    });
  } else if (field.type === 'instance') {
    const instances = instancesForField(catalog, collected, field.instanceFilter || 'all');
    const appName = findApp(catalog, collected.application_id || '')?.name || 'the selected application';
    if (!instances.length) {
      opts.push({
        id: 'action_add_instance',
        label: 'Add environment now',
        description: `Create an environment for ${appName} step by step here`,
        value: '__action_add_instance__',
      });
      opts.push({
        id: 'action_change_app',
        label: 'Choose a different application',
        description: 'Go back and pick another app from your catalog',
        value: '__action_change_application__',
      });
      opts.push({
        id: 'action_refresh',
        label: 'Refresh list',
        description: 'Reload instances after you add an environment',
        value: '__action_refresh_catalog__',
      });
    } else {
      const items = filterListItems(collectWizardListItems(field, catalog, collected, context), listFilter);
      const pageCount = Math.max(1, Math.ceil(items.length / WIZARD_LIST_PAGE_SIZE));
      const page = Math.min(Math.max(1, requestedPage), pageCount);
      const start = (page - 1) * WIZARD_LIST_PAGE_SIZE;
      for (const item of items.slice(start, start + WIZARD_LIST_PAGE_SIZE)) {
        opts.push({ id: item.id, label: item.label, description: item.description, value: item.value });
      }
      appendListNavOptions(opts, page, pageCount, listFilter, items.length);
      opts.push({
        id: 'action_add_instance',
        label: 'Add environment now',
        description: `Create a new environment for ${appName}`,
        value: '__action_add_instance__',
      });
    }
  } else if (isWizardChipListField(field)) {
    const items = filterListItems(collectWizardListItems(field, catalog, collected, context), listFilter);
    const pageCount = Math.max(1, Math.ceil(Math.max(items.length, 1) / WIZARD_LIST_PAGE_SIZE));
    const page = Math.min(Math.max(1, requestedPage), pageCount);
    const start = (page - 1) * WIZARD_LIST_PAGE_SIZE;
    for (const item of items.slice(start, start + WIZARD_LIST_PAGE_SIZE)) {
      opts.push({ id: item.id, label: item.label, description: item.description, value: item.value });
    }
    appendListNavOptions(opts, page, pageCount, listFilter, items.length);
    if (field.type === 'application') {
      opts.push({
        id: 'action_browse_apps',
        label: 'Refresh catalog',
        description: 'Reload applications after someone adds one',
        value: '__action_refresh_catalog__',
      });
    }
  }

  if (!field.required) {
    opts.push({ id: 'wz_skip', label: 'Skip', value: '__skip__' });
  }
  opts.push({ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' });
  return opts;
}

function normalizeLookupKey(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

const KNOWN_ENV_LABELS: Record<string, string> = {
  production: 'Production',
  prod: 'Production',
  staging: 'Staging',
  stage: 'Staging',
  development: 'Development',
  dev: 'Development',
  test: 'Test',
  testing: 'Testing',
  qa: 'Test',
  uat: 'UAT',
  sandbox: 'Development',
};

/** User wants a new environment created (not pick an existing chip). */
export function isCreateInstanceIntent(raw: string): boolean {
  const t = raw.trim().toLowerCase();
  if (!t) return false;
  return (
    /\b(create|add|make|new)\b.{0,40}\b(instance|environment|env)\b/i.test(t)
    || /^(create|add)\s+(a\s+|an\s+|the\s+)?(new\s+)?(one|it)\b/i.test(t)
    || /\badd\s+environment\s+now\b/i.test(t)
    || /\bnew\s+(instance|environment|env)\b/i.test(t)
  );
}

export function mapKnownEnvironmentLabel(raw: string): string | undefined {
  return KNOWN_ENV_LABELS[normalizeLookupKey(raw)];
}

/**
 * Prefill for the add-environment subflow from free text like
 * "create Staging", "add new staging instance", or just "Staging".
 */
export function extractNewInstanceHint(raw: string): Record<string, string> {
  const trimmed = raw.trim();
  if (!trimmed) return {};

  let rest = trimmed
    .replace(
      /^(please\s+)?(can you\s+)?(create|add|make)\s+(a\s+|an\s+|the\s+)?(new\s+)?(environment|instance|env)\b/i,
      '',
    )
    .replace(/^(please\s+)?(create|add|make)\s+/i, '')
    .replace(/\s+for\s+(this|it|me|the\s+app|that)\s*$/i, '')
    .replace(/^(called|named|as)\s+/i, '')
    .trim();

  if (!rest || /^(this|it|one|please|now)$/i.test(rest)) {
    return {};
  }

  // "staging instance" / "Production env"
  rest = rest.replace(/\s+(instance|environment|env)\s*$/i, '').trim();
  if (!rest) return {};

  const env = mapKnownEnvironmentLabel(rest);
  if (env) {
    return { new_instance_name: env, new_instance_environment: env };
  }

  return { new_instance_name: rest.slice(0, 80) };
}

/** Unmatched free text that should create an environment instead of looping "pick from list". */
export function shouldCreateInstanceFromInput(
  raw: string,
  existing: Array<{ id: string; name: string; environment: string }>,
): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (isCreateInstanceIntent(trimmed)) return true;

  const env = mapKnownEnvironmentLabel(trimmed);
  if (!env) return false;

  const taken = existing.some(
    i =>
      normalizeLookupKey(i.name) === normalizeLookupKey(env)
      || normalizeLookupKey(i.environment) === normalizeLookupKey(env),
  );
  return !taken;
}

function findAppByUserInput(catalog: VysorAppCatalogItem[], input: string): VysorAppCatalogItem | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  const byId = catalog.find(a => a.id === trimmed);
  if (byId) return byId;

  const norm = normalizeLookupKey(trimmed);
  const exact = catalog.filter(
    a =>
      normalizeLookupKey(a.name) === norm
      || (a.application_slug ? normalizeLookupKey(a.application_slug) === norm : false),
  );
  if (exact.length === 1) return exact[0];

  const partial = catalog.filter(a => {
    const nameKey = normalizeLookupKey(a.name);
    const slugKey = a.application_slug ? normalizeLookupKey(a.application_slug) : '';
    return nameKey.includes(norm) || norm.includes(nameKey) || (slugKey && (slugKey.includes(norm) || norm.includes(slugKey)));
  });
  if (partial.length === 1) return partial[0];

  return undefined;
}

function findInstanceByUserInput(
  catalog: VysorAppCatalogItem[],
  collected: Record<string, string>,
  filter: 'all' | 'pending-sso',
  input: string,
) {
  const instances = instancesForField(catalog, collected, filter);
  const trimmed = input.trim();
  if (!trimmed) return null;

  const byId = findInstance(catalog, trimmed);
  if (byId && instances.some(i => i.id === byId.inst.id)) return byId;

  const norm = normalizeLookupKey(trimmed);
  const matches = instances.filter(inst => {
    const label = `${inst.name} (${inst.environment})`;
    return (
      label.toLowerCase() === trimmed.toLowerCase()
      || normalizeLookupKey(inst.name) === norm
      || normalizeLookupKey(inst.environment) === norm
      || normalizeLookupKey(label) === norm
    );
  });
  if (matches.length === 1) return findInstance(catalog, matches[0].id);

  const partial = instances.filter(inst => {
    const nameKey = normalizeLookupKey(inst.name);
    return nameKey.includes(norm) || norm.includes(nameKey);
  });
  if (partial.length === 1) return findInstance(catalog, partial[0].id);

  return null;
}

function findIdpByUserInput(idps: VysorIdpOption[], input: string, optionValue?: string): VysorIdpOption | undefined {
  const trimmed = input.trim();
  const value = optionValue ?? trimmed;
  const byValue = idps.find(p => p.id === value);
  if (byValue) return byValue;

  const norm = normalizeLookupKey(trimmed);
  const byLabel = idps.find(
    p => p.label.toLowerCase() === trimmed.toLowerCase() || normalizeLookupKey(p.label) === norm || normalizeLookupKey(p.id) === norm,
  );
  if (byLabel) return byLabel;

  const partial = idps.filter(p => {
    const labelKey = normalizeLookupKey(p.label);
    return labelKey.includes(norm) || norm.includes(labelKey);
  });
  if (partial.length === 1) return partial[0];

  return undefined;
}

function matchChoiceOption(field: WizardField, rawAnswer: string, optionValue?: string): string | undefined {
  if (!field.options?.length) return undefined;
  const trimmed = rawAnswer.trim();
  const value = optionValue ?? trimmed;
  const exact = field.options.find(o => o === value || o.toLowerCase() === trimmed.toLowerCase());
  if (exact) return exact;

  const norm = normalizeLookupKey(trimmed);
  const aliases: Record<string, Record<string, string>> = {
    business_function: {
      hr: 'HR',
      humanresources: 'HR',
      finance: 'Finance',
      fin: 'Finance',
      engineering: 'Engineering',
      eng: 'Engineering',
      it: 'IT',
      informationtechnology: 'IT',
      sales: 'Sales',
      marketing: 'Marketing',
      operations: 'Operations',
      ops: 'Operations',
    },
    risk_level: {
      low: 'Low',
      medium: 'Medium',
      med: 'Medium',
      high: 'High',
      critical: 'Critical',
      crit: 'Critical',
      notdefined: 'Not Defined',
      undefined: 'Not Defined',
    },
    entitlement_type: {
      role: 'Role',
      permission: 'Permission',
      permissions: 'Permission',
      perm: 'Permission',
      group: 'Group',
      access: 'Access',
      resource: 'Resource',
    },
    new_instance_environment: {
      prod: 'Production',
      production: 'Production',
      dev: 'Development',
      development: 'Development',
      stage: 'Staging',
      staging: 'Staging',
      test: 'Test',
      qa: 'Test',
    },
    sso_type: {
      unsure: 'Not sure',
      idk: 'Not sure',
      dontknow: 'Not sure',
      notsure: 'Not sure',
      saml2: 'SAML',
      openid: 'OIDC',
      oauth: 'OIDC',
      oauth2: 'OIDC',
    },
  };
  const fieldAliases = aliases[field.key];
  if (fieldAliases) {
    const mapped = fieldAliases[norm];
    if (mapped && field.options.includes(mapped)) return mapped;
  }

  const normalizedMatch = field.options.filter(o => normalizeLookupKey(o) === norm);
  if (normalizedMatch.length === 1) return normalizedMatch[0];

  return undefined;
}

export const SSO_TYPE_MISMATCH_HINT =
  'Choose **SAML**, **OIDC**, or **Not sure** (chip or type the label). ' +
  'You can also paste a **metadata URL** or SAML XML here — I will detect SAML and fetch it for you.';

/** Pull a metadata URL or XML blob out of free-form chat text. */
export function extractMetadataCandidate(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (isMetadataXmlContent(trimmed)) return trimmed;
  const urlMatch = trimmed.match(/https?:\/\/[^\s<>"']+/i);
  if (urlMatch) {
    return urlMatch[0].replace(/[.,;:)]+$/, '');
  }
  return null;
}

/** Infer SAML/OIDC when the user describes the app instead of naming a protocol. */
export function inferSsoTypeFromFreeText(text: string): 'SAML' | 'OIDC' | null {
  const lower = text.toLowerCase();
  if (/\boidc\b|openid\s*connect|\boauth2?\b/.test(lower)) return 'OIDC';
  if (
    /\bsaml\b|federationmetadata|metadata\.xml/.test(lower)
    || /\b(salesforce|sap\b|workday|servicenow|concur|sharepoint|dynamics)\b/.test(lower)
  ) {
    return 'SAML';
  }
  return null;
}

function nextWizardStepIndex(wizard: WizardState, collected: Record<string, string>, fromIndex: number): number {
  let nextIndex = fromIndex + 1;
  const hasMetadata = Boolean(
    collected.metadata_content?.trim()
    || collected.metadata_entity_id?.trim()
    || collected.metadata_acs_url?.trim()
    || collected.metadata_url?.trim()
    || collected.oidc_discovery_endpoint?.trim()
    || collected.redirect_uris?.trim(),
  );
  if (hasMetadata) {
    while (nextIndex < wizard.flow.fields.length && wizard.flow.fields[nextIndex].key === 'metadata_or_notes') {
      nextIndex += 1;
    }
  }
  return nextIndex;
}

function buildWizardAdvanceResult(
  wizard: WizardState,
  collected: Record<string, string>,
  nextIndex: number,
  display: string,
  context: VysorWizardContext = {},
): WizardAdvanceResult {
  const skipped = autoFillAndSkipIdpPlatform(
    wizard,
    collected,
    nextIndex,
    context.configuredIdps,
  );
  const finalCollected = skipped.collected;
  const finalIndex = skipped.nextIndex;

  if (finalIndex >= wizard.flow.fields.length) {
    if (wizard.flow.id === 'configure-sso') {
      return {
        kind: 'complete-sso-chat',
        collected: finalCollected,
        userFacingAnswer: display,
        ssoChatMessage: buildSsoWizardChatMessage(finalCollected),
        ssoPrefill: buildSsoPrefillFromWizard(finalCollected),
        storagePayload: finalCollected,
      };
    }
    return {
      kind: 'complete-navigate',
      collected: finalCollected,
      userFacingAnswer: display,
      storagePayload: buildWizardStoragePayload(wizard.flow.id, finalCollected),
    };
  }

  return {
    kind: 'next',
    collected: finalCollected,
    nextField: wizard.flow.fields[finalIndex],
    nextStepIndex: finalIndex,
    userFacingAnswer: display,
  };
}

/**
 * Skip questions the system can answer (IdP, slug, risk/status, env type, entitlement type).
 * Same idea as IdP auto-detect — don't ask app owners for known defaults.
 */
function applySmartWizardDefaults(
  wizard: WizardState,
  collected: Record<string, string>,
  nextIndex: number,
  contextIdps?: VysorIdpOption[],
): { collected: Record<string, string>; nextIndex: number } {
  let c = { ...collected };
  let i = nextIndex;
  const max = wizard.flow.fields.length;
  let guard = 0;
  while (i < max && guard++ < 12) {
    const field = wizard.flow.fields[i];
    if (!field) break;

    if ((field.type === 'idp-platform' || field.key === 'platform') && !c.platform_id?.trim()) {
      const idps = contextIdps || [];
      const protocol = c.sso_type === 'OIDC' ? 'OIDC' : c.sso_type === 'SAML' ? 'SAML' : null;
      if (realConfiguredIdps(idps).length || protocol) {
        const platformId = pickDefaultIdpPlatform(idps, protocol);
        const label = idpPlatformLabel(idps, platformId);
        c = { ...c, platform_id: platformId, platform: label, [field.key]: platformId };
        i += 1;
        continue;
      }
    }

    if (field.key === 'application_id' && !c.application_id?.trim() && c.name?.trim()) {
      const slug = suggestApplicationSlug(c.name);
      if (!validateApplicationSlug(slug)) {
        c = { ...c, application_id: slug };
        i += 1;
        continue;
      }
    }

    if (field.key === 'business_function' && !c.business_function?.trim() && c.name?.trim()) {
      const inferred = inferBusinessFunctionFromName(c.name);
      if (inferred && (inferred.confidence === 'high' || inferred.confidence === 'medium')) {
        c = { ...c, business_function: inferred.value };
        i += 1;
        continue;
      }
    }

    if (field.key === 'description' && !field.required && !c.description?.trim()) {
      c = { ...c, description: '' };
      i += 1;
      continue;
    }

    if (field.key === 'risk_level' && !c.risk_level?.trim()) {
      c = { ...c, risk_level: 'Not Defined' };
      i += 1;
      continue;
    }

    if (field.key === 'status' && !c.status?.trim()) {
      c = { ...c, status: 'Active' };
      i += 1;
      continue;
    }

    if (field.key === 'entitlement_type' && !c.entitlement_type?.trim()) {
      c = { ...c, entitlement_type: 'Role' };
      i += 1;
      continue;
    }

    if (
      field.key === 'new_instance_environment' &&
      !c.new_instance_environment?.trim() &&
      c.new_instance_name?.trim()
    ) {
      const env = mapKnownEnvironmentLabel(c.new_instance_name);
      if (env) {
        c = { ...c, new_instance_environment: env, new_instance_name: c.new_instance_name || env };
        i += 1;
        continue;
      }
    }

    break;
  }
  return { collected: c, nextIndex: i };
}

function autoFillAndSkipIdpPlatform(
  wizard: WizardState,
  collected: Record<string, string>,
  nextIndex: number,
  contextIdps?: VysorIdpOption[],
): { collected: Record<string, string>; nextIndex: number } {
  return applySmartWizardDefaults(wizard, collected, nextIndex, contextIdps);
}

function findInstance(catalog: VysorAppCatalogItem[], instanceId: string) {
  for (const app of catalog) {
    const inst = app.instances.find(i => i.id === instanceId);
    if (inst) return { app, inst };
  }
  return null;
}

function resolveFieldValue(
  field: WizardField,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  catalog: VysorAppCatalogItem[],
  context: VysorWizardContext,
  collected: Record<string, string>,
): { ok: false; reason: 'cancel' | 'invalid' | 'action'; action?: VysorWizardAction; invalidHint?: string }
  | { ok: true; stored: Record<string, string>; display: string } {
  const trimmed = rawAnswer.trim();
  const value = optionValue ?? trimmed;
  const id = optionId ?? '';

  // Cancel / skip only via chips — typed "cancel" or "skip" may be legitimate field values.
  if (id === 'wz_cancel' || value === '__cancel__') {
    return { ok: false, reason: 'cancel' };
  }

  const action = WIZARD_ACTION_VALUES[value];
  if (action) {
    return { ok: false, reason: 'action', action };
  }

  if (id === 'wz_skip' || value === '__skip__') {
    if (field.required) return { ok: false, reason: 'invalid', invalidHint: 'This step cannot be skipped.' };
    return { ok: true, stored: { [field.key]: '' }, display: 'Skip' };
  }

  if (field.type === 'application') {
    if (!catalog.length) {
      return {
        ok: false,
        reason: 'invalid',
        invalidHint: 'No applications in your catalog yet. Use one of the actions above to add one, or refresh after you do.',
      };
    }
    const app = catalog.find(a => a.id === value) || findAppByUserInput(catalog, trimmed);
    if (!app) {
      return {
        ok: false,
        reason: 'invalid',
        invalidHint: 'Pick an application from the list, type its exact name, or use Refresh catalog.',
      };
    }
    return {
      ok: true,
      stored: { application_id: app.id, application_name: app.name, [field.key]: app.id },
      display: app.name,
    };
  }

  if (field.type === 'instance') {
    // Free-text "create/add environment" (or an unmatched name like Staging) → create flow
    if (
      !optionId
      && trimmed
      && shouldCreateInstanceFromInput(
        trimmed,
        instancesForField(catalog, collected, field.instanceFilter || 'all'),
      )
    ) {
      return { ok: false, reason: 'action', action: 'add_instance' };
    }

    const instances = instancesForField(catalog, collected, field.instanceFilter || 'all');
    if (!instances.length) {
      return {
        ok: false,
        reason: 'invalid',
        invalidHint:
          'No environments yet for this app. Say **create Staging** (or any name), or tap **Add environment now**.',
      };
    }
    const hit = findInstance(catalog, value) || findInstanceByUserInput(catalog, collected, field.instanceFilter || 'all', trimmed);
    if (!hit) {
      return {
        ok: false,
        reason: 'invalid',
        invalidHint:
          `I don't see **${trimmed}** yet. Say **create ${trimmed}** and I'll add it, tap **Add environment now**, or pick an existing environment below.`,
      };
    }
    const label = `${hit.inst.name} (${hit.inst.environment})`;
    return {
      ok: true,
      stored: { instance_id: hit.inst.id, instance_name: label, [field.key]: hit.inst.id },
      display: label,
    };
  }

  if (field.type === 'idp-platform') {
    const idps = context.configuredIdps?.length
      ? context.configuredIdps
      : [
          { id: 'generic_saml', label: 'Generic SAML' },
          { id: 'generic_oidc', label: 'Generic OIDC' },
        ];
    const idp = findIdpByUserInput(idps, trimmed, optionValue);
    if (!idp) {
      return {
        ok: false,
        reason: 'invalid',
        invalidHint: 'Choose an identity provider from the list or type its name (e.g. Generic SAML).',
      };
    }
    return {
      ok: true,
      stored: { platform_id: idp.id, platform: idp.label, [field.key]: idp.id },
      display: idp.label,
    };
  }

  if (field.type === 'choice' && field.options?.length) {
    if (field.key === 'sso_type') {
      const metaCandidate = extractMetadataCandidate(trimmed);
      if (metaCandidate && isMetadataXmlContent(metaCandidate)) {
        try {
          const parsed = parseSamlMetadataContent(metaCandidate);
          return {
            ok: true,
            stored: {
              sso_type: 'SAML',
              metadata_or_notes: metaCandidate,
              metadata_content: parsed.content.slice(0, 8000),
              metadata_entity_id: parsed.entityId || '',
              metadata_acs_url: parsed.acsUrl || '',
              metadata_has_certificate: String(parsed.hasCertificate),
            },
            display: 'SAML (from metadata)',
          };
        } catch {
          return {
            ok: false,
            reason: 'invalid',
            invalidHint: 'That XML does not look like valid SAML metadata. Paste a metadata URL or choose SAML/OIDC.',
          };
        }
      }
      const inferred = inferSsoTypeFromFreeText(trimmed);
      if (inferred) {
        return { ok: true, stored: { sso_type: inferred }, display: inferred };
      }
    }
    if (field.key === 'business_function') {
      const customFromOther = trimmed.match(/^other[:\-\s]+(.+)$/i)?.[1]?.trim();
      if (customFromOther) {
        return { ok: true, stored: { [field.key]: customFromOther }, display: customFromOther };
      }
    }
    const matched = matchChoiceOption(field, trimmed, optionValue);
    if (!matched) {
      if (field.key === 'sso_type') {
        return { ok: false, reason: 'invalid', invalidHint: SSO_TYPE_MISMATCH_HINT };
      }
      const labels = field.options.join(', ');
      return {
        ok: false,
        reason: 'invalid',
        invalidHint: `Choose one of: ${labels}. You can click a chip or type the same label.`,
      };
    }
    if (field.key === 'sso_type' && matched === 'Not sure') {
      return {
        ok: false,
        reason: 'invalid',
        invalidHint:
          'Click **Not sure** or tell me what you have (metadata URL, vendor guide) — I will help you pick SAML or OIDC.',
      };
    }
    if (field.key === 'business_function' && matched === 'Other') {
      const custom = trimmed
        .replace(/^other[:\-\s]*/i, '')
        .trim();
      if (!custom || custom.toLowerCase() === 'other') {
        return {
          ok: false,
          reason: 'invalid',
          invalidHint: 'For **Other**, type a custom business function (example: `Other: Legal`).',
        };
      }
      return { ok: true, stored: { [field.key]: custom }, display: custom };
    }
    return { ok: true, stored: { [field.key]: matched }, display: matched };
  }

  if (field.required && !trimmed) {
    return { ok: false, reason: 'invalid', invalidHint: 'This field is required.' };
  }

  if (field.key === 'application_id') {
    const slugError = validateApplicationSlug(trimmed);
    if (slugError) {
      return { ok: false, reason: 'invalid', invalidHint: slugError };
    }
    const slug = normalizeApplicationSlug(trimmed);
    return { ok: true, stored: { [field.key]: slug }, display: slug };
  }

  return { ok: true, stored: { [field.key]: trimmed }, display: trimmed };
}

export function advanceVysorWizard(
  wizard: WizardState,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  catalog: VysorAppCatalogItem[],
  context: VysorWizardContext = {},
): WizardAdvanceResult {
  const current = wizard.flow.fields[wizard.stepIndex];

  if (isWizardChipListField(current)) {
    // Empty instance/application lists only expose guided actions — skip list nav.
    const hasSelectableItems = collectWizardListItems(current, catalog, wizard.collected, context).length > 0;
    if (hasSelectableItems) {
      const listControl = resolveWizardListControl(
        wizard,
        current,
        rawAnswer,
        optionId,
        optionValue,
        catalog,
        context,
      );
      if (listControl) return listControl;
    }
  }

  const resolved = resolveFieldValue(
    current,
    rawAnswer,
    optionId,
    optionValue,
    catalog,
    context,
    wizard.collected,
  );

  if (!resolved.ok) {
    if (resolved.reason === 'cancel') return { kind: 'cancelled' };
    if (resolved.reason === 'action' && resolved.action) {
      let collected = wizard.collected;
      if (resolved.action === 'add_instance') {
        const hint = extractNewInstanceHint(rawAnswer);
        if (Object.keys(hint).length) {
          collected = { ...wizard.collected, ...hint };
        }
      }
      return { kind: 'guided-action', action: resolved.action, collected };
    }
    return { kind: 'invalid', invalidHint: resolved.invalidHint };
  }

  const collected = { ...wizard.collected, ...resolved.stored };
  const nextIndex = nextWizardStepIndex(wizard, collected, wizard.stepIndex);

  return buildWizardAdvanceResult(wizard, collected, nextIndex, resolved.display, context);
}

function resolveWizardListControl(
  wizard: WizardState,
  field: WizardField,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  catalog: VysorAppCatalogItem[],
  context: VysorWizardContext,
): WizardAdvanceResult | null {
  const trimmed = rawAnswer.trim();
  const value = optionValue ?? trimmed;
  const id = optionId ?? '';
  const filter = wizardListFilter(wizard);
  const items = filterListItems(collectWizardListItems(field, catalog, wizard.collected, context), filter);
  const pageCount = Math.max(1, Math.ceil(Math.max(items.length, 1) / WIZARD_LIST_PAGE_SIZE));
  const page = Math.min(Math.max(1, wizardListPage(wizard)), pageCount);

  const repaint = (nextPage: number, nextFilter: string | undefined, awaiting = false): WizardAdvanceResult => ({
    kind: 'repaint-options',
    collected: wizard.collected,
    nextField: field,
    nextStepIndex: wizard.stepIndex,
    wizardPatch: {
      listPage: nextPage,
      listFilter: nextFilter,
      awaitingListFilter: awaiting,
      catalogPage: nextPage,
      catalogFilter: nextFilter,
      awaitingCatalogFilter: awaiting,
    },
    pageMessage: buildWizardListPickerMessage(field, catalog, wizard.collected, context, nextPage, nextFilter),
  });

  if (wizardAwaitingListFilter(wizard) && !optionId && !WIZARD_ACTION_VALUES[value] && value !== '__cancel__') {
    if (!trimmed) {
      return {
        kind: 'repaint-options',
        collected: wizard.collected,
        nextField: field,
        nextStepIndex: wizard.stepIndex,
        wizardPatch: {
          awaitingListFilter: true,
          awaitingCatalogFilter: true,
        },
        pageMessage: 'Type part of a name to filter this list (example: `prod`), or tap **Cancel**.',
      };
    }
    // Prefer selecting a real value (or starting a guided action) over treating the text as a filter.
    const probe = resolveFieldValue(field, trimmed, undefined, undefined, catalog, context, wizard.collected);
    if (probe.ok) return null;
    if (!probe.ok && (probe.reason === 'action' || probe.reason === 'cancel')) return null;
    return repaint(1, trimmed, false);
  }

  const isNext =
    id === 'wz_list_next'
    || id === 'wz_catalog_next'
    || value === '__list_next__'
    || value === '__catalog_next__'
    || /^show next(\s*\(|$)/i.test(trimmed);
  const isPrev =
    id === 'wz_list_prev'
    || id === 'wz_catalog_prev'
    || value === '__list_prev__'
    || value === '__catalog_prev__'
    || /^show previous(\s*\(|$)/i.test(trimmed);
  const isFilter =
    id === 'wz_list_filter'
    || id === 'wz_catalog_filter'
    || value === '__list_filter__'
    || value === '__catalog_filter__'
    || /^filter by name$/i.test(trimmed);
  const isClear =
    id === 'wz_list_clear_filter'
    || id === 'wz_catalog_clear_filter'
    || value === '__list_clear_filter__'
    || value === '__catalog_clear_filter__'
    || /^clear filter$/i.test(trimmed);

  if (isNext) return repaint(Math.min(page + 1, pageCount), filter);
  if (isPrev) return repaint(Math.max(1, page - 1), filter);
  if (isFilter) {
    return {
      kind: 'repaint-options',
      collected: wizard.collected,
      nextField: field,
      nextStepIndex: wizard.stepIndex,
      wizardPatch: {
        awaitingListFilter: true,
        awaitingCatalogFilter: true,
      },
      pageMessage:
        'Type part of a **name** to narrow this list (same idea as pending SSO filter). You can still tap **Cancel** to exit.',
    };
  }
  if (isClear) return repaint(1, undefined);

  return null;
}

/** Advance SSO wizard after protocol + metadata were inferred from a URL (skips metadata step when prefilled). */
export function advanceSsoWizardAfterProtocolPrefill(
  wizard: WizardState,
  collected: Record<string, string>,
  display: string,
  context: VysorWizardContext = {},
): WizardAdvanceResult {
  const nextIndex = nextWizardStepIndex(wizard, collected, wizard.stepIndex);
  return buildWizardAdvanceResult(wizard, collected, nextIndex, display, context);
}

export function wizardAnswerFromOption(opt: WizardQuickOption): { answer: string; optionId: string; optionValue: string } {
  return { answer: opt.label, optionId: opt.id, optionValue: opt.value };
}

export type InstanceSubFlowResult =
  | { kind: 'cancelled' }
  | { kind: 'invalid'; invalidHint?: string }
  | { kind: 'subflow-next'; collected: Record<string, string>; nextStepIndex: number; nextField: WizardField; userFacingAnswer: string }
  | { kind: 'subflow-complete'; collected: Record<string, string>; userFacingAnswer: string };

/** Advance the inline add-environment sub-wizard (stays inside VYSOR). */
export function advanceInstanceSubWizard(
  stepIndex: number,
  collected: Record<string, string>,
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
): InstanceSubFlowResult {
  const field = ADD_INSTANCE_SUBFLOW_FIELDS[stepIndex];
  const resolved = resolveFieldValue(field, rawAnswer, optionId, optionValue, [], {}, collected);

  if (!resolved.ok) {
    if (resolved.reason === 'cancel') return { kind: 'cancelled' };
    if (resolved.reason === 'action') {
      return { kind: 'invalid', invalidHint: 'Finish adding the environment, or choose Cancel to go back.' };
    }
    return { kind: 'invalid', invalidHint: resolved.invalidHint };
  }

  const nextCollected = { ...collected, ...resolved.stored };
  let nextIndex = stepIndex + 1;

  if (
    ADD_INSTANCE_SUBFLOW_FIELDS[stepIndex]?.key === 'new_instance_name' &&
    nextCollected.new_instance_name &&
    !nextCollected.new_instance_environment
  ) {
    const env = mapKnownEnvironmentLabel(nextCollected.new_instance_name);
    if (env) {
      nextCollected.new_instance_environment = env;
      if (ADD_INSTANCE_SUBFLOW_FIELDS[nextIndex]?.key === 'new_instance_environment') {
        nextIndex += 1;
      }
    }
  }

  if (nextIndex >= ADD_INSTANCE_SUBFLOW_FIELDS.length) {
    return { kind: 'subflow-complete', collected: nextCollected, userFacingAnswer: resolved.display };
  }

  return {
    kind: 'subflow-next',
    collected: nextCollected,
    nextStepIndex: nextIndex,
    nextField: ADD_INSTANCE_SUBFLOW_FIELDS[nextIndex],
    userFacingAnswer: resolved.display,
  };
}

export function stripNewInstanceFields(collected: Record<string, string>): Record<string, string> {
  const next = { ...collected };
  delete next.new_instance_name;
  delete next.new_instance_environment;
  delete next.new_instance_description;
  return next;
}

export function resumeWizardAfterInstanceCreated(
  wizard: WizardState,
  instance: { id: string; name: string; environment: string },
): { wizard: WizardState; nextField: WizardField } {
  const instanceStepIndex = wizard.flow.fields.findIndex(f => f.type === 'instance');
  const nextStepIndex = instanceStepIndex >= 0 ? instanceStepIndex + 1 : wizard.stepIndex + 1;
  const label = `${instance.name} (${instance.environment})`;
  const collected = {
    ...stripNewInstanceFields(wizard.collected),
    instance_id: instance.id,
    instance_name: label,
  };
  return {
    wizard: {
      flow: wizard.flow,
      stepIndex: nextStepIndex,
      collected,
    },
    nextField: wizard.flow.fields[nextStepIndex],
  };
}

/** Rewind wizard to application picker (e.g. after "choose different application"). */
export function rewindWizardToApplicationStep(wizard: WizardState): WizardState {
  const collected = { ...wizard.collected };
  delete collected.application_id;
  delete collected.application_name;
  delete collected.instance_id;
  delete collected.instance_name;
  const appFieldIndex = wizard.flow.fields.findIndex(f => f.type === 'application');
  return {
    flow: wizard.flow,
    stepIndex: appFieldIndex >= 0 ? appFieldIndex : 0,
    collected,
    ...clearListPagingState(),
  };
}
